/**
 * dc-runtime style helpers — the single source of truth for how a reference style literal becomes a React style object.
 * `apps/web/src/lib/dc.tsx` re-exports these so the generated views and this library build byte-identical style attributes.
 * Mirrors design-reference/support.js (`cssToObj`, `compileAttr` join semantics).
 */
import type { CSSProperties } from 'react';

const kebabToCamel = (p: string) => p.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
const cache = new Map<string, CSSProperties>();

/** cssToObj: split on ';', camelCase properties, keep custom properties as-is. Declaration order is preserved (React serialises in insertion order). */
export function css(text: string): CSSProperties {
  const hit = cache.get(text);
  if (hit) return hit;
  const o: Record<string, string> = {};
  for (const decl of text.split(';')) {
    const i = decl.indexOf(':');
    if (i < 0) continue;
    const prop = decl.slice(0, i).trim();
    o[prop.startsWith('--') ? prop : kebabToCamel(prop)] = decl.slice(i + 1).trim();
  }
  const out = o as CSSProperties;
  if (cache.size < 5000) cache.set(text, out);
  return out;
}
/** Mixed attribute interpolation part: null/undefined → ''. */
export function s(value: unknown): string {
  return value === undefined || value === null ? '' : String(value as string | number | boolean);
}
/** Whole-value style binding: strings go through cssToObj, objects pass through. */
export function cssAny(value: unknown): CSSProperties | undefined {
  if (typeof value === 'string') return css(value);
  if (value && typeof value === 'object') return value as CSSProperties;
  return undefined;
}
/** Generic attribute binding: null → undefined (React drops it). */
export function a(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  return String(value as string | number | boolean);
}
/** Booleanish aria-* binding. */
export function b(value: unknown): boolean | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'boolean') return value;
  return String(value as string | number) !== 'false';
}
/** Number-only DOM props (tabIndex, rows…). */
export function n(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const x = Number(value as string | number);
  return Number.isNaN(x) ? undefined : x;
}
/** SVG transform: the reference passes the CSS keyword 'none', which is invalid there — omit it (same render). */
export function tx(value: unknown): string | undefined {
  const v = a(value);
  return v === undefined || v === 'none' ? undefined : v;
}
export function cx(...parts: unknown[]): string {
  return parts
    .filter((p) => p !== undefined && p !== null && p !== false && p !== '')
    .map((p) => String(p as string | number | boolean))
    .join(' ');
}
/** Compose a style literal with optional extra declarations, preserving declaration order (base first). */
export function styleOf(base: string, extra?: string): CSSProperties {
  return css(extra ? `${base};${extra}` : base);
}
